import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FaMicrophone, FaLeaf } from "react-icons/fa";
import { BsPauseFill } from "react-icons/bs";
import { IoMdClose } from "react-icons/io";
import { IoSend } from "react-icons/io5";
import { FiDownload, FiSettings, FiChevronDown, FiBarChart2, FiHelpCircle } from "react-icons/fi";
import {
  ArrowIcon,
  DownloadIcon,
  DownArrowIcon,
  isTokenExpired,
  handleDownloadPDF,
  startRecording,
  stopRecording,
  handleRecordingStop,
  handleMicClick,
} from "./HelperFuncs";
import "./AnalyzeMe.css";
import logoSvg from './assets/logo.svg';

const { getStarter } = require("./convoStarters");

function AnalyzeMePage() {
  // State
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isLoadingResp, setIsLoadingResp] = useState(false);
  const [isResponding, setIsResponding] = useState(false);
  const [theme, setTheme] = useState("light");
  const [recording, setRecording] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isVoiceNote, setIsVoiceNote] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordedTranscription, setRecordedTranscription] = useState(null);

  // Analysis-specific state
  const [showAnalysisButton, setShowAnalysisButton] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [processingAnalysis, setProcessingAnalysis] = useState(false);
  const [showAnalysisResults, setShowAnalysisResults] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // GUEST MODE (always enabled for analyze)
  const [guestId, setGuestId] = useState(null);
  const [guestAgreedToTos, setGuestAgreedToTos] = useState(false);
  const [showGuestTosModal, setShowGuestTosModal] = useState(false);
  const [pendingGuestMessage, setPendingGuestMessage] = useState(null);

  // Show/hide scroll-down button
  const [showScrollDown, setShowScrollDown] = useState(false);

  // Refs
  const chatWindowRef = useRef(null);
  const inputRef = useRef(null);
  const settingsRef = useRef(null);
  const settingsBtnRef = useRef(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const recordingTimeoutRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const displayIntervalRef = useRef(null); // Track AI response display interval

  // Group messages by sender for better UI
  const groupedMessages = useMemo(() => {
    const groups = [];
    let currentGroup = null;

    messages.forEach((message) => {
      if (!currentGroup || currentGroup.sender !== message.sender) {
        currentGroup = { sender: message.sender, messages: [message] };
        groups.push(currentGroup);
      } else {
        currentGroup.messages.push(message);
      }
    });

    return groups;
  }, [messages]);

  // Auto-adjust textarea height
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "40px";
      const scrollHeight = inputRef.current.scrollHeight;
      inputRef.current.style.height = Math.min(scrollHeight, 150) + "px";
    }
  }, [inputText]);

  // Effect to initialize guest mode
  useEffect(() => {
    initializeGuestMode();

    // Set theme from localStorage if available
    const savedTheme = localStorage.getItem("analyzeTheme");
    if (savedTheme) {
      setTheme(savedTheme);
    }
  }, []);

  // Add click outside listener for settings
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isSettingsOpen &&
        settingsRef.current &&
        !settingsRef.current.contains(event.target) &&
        settingsBtnRef.current &&
        !settingsBtnRef.current.contains(event.target)
      ) {
        setIsSettingsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSettingsOpen]);

  // Show analysis button when user message count reaches 5
  useEffect(() => {
    const userMessageCount = Math.floor(messages.length / 2);
    if (userMessageCount >= 5 && !showAnalysisButton) {
      setShowAnalysisButton(true);
    }
  }, [messages.length, showAnalysisButton]);

  // Fix mobile 100vh
  useEffect(() => {
    const updateVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    updateVH();
    window.addEventListener("resize", updateVH);
    window.addEventListener("orientationchange", () => {
      setTimeout(updateVH, 100);
    });
    return () => {
      window.removeEventListener("resize", updateVH);
      window.removeEventListener("orientationchange", updateVH);
    };
  }, []);

  // Initial AI message
  useEffect(() => {
    const initialAIMessage = {
      sender: "ai",
      text: "Welcome to Analysis Mode. I am your confidential AI profiler. None of this data is saved in any way and will permenently be lost when you leave this page.\n\nI will ask you a series of questions in order to gain a thorough understanding of you and then after a certain amount of messages you can submit our conversation to get a very detailed summary of where you are at psychologically.\n\nThis will be a detailed breakdown of who you are and will include strengths, weaknesses, blind spots, relationship observations, practical areas to improve in and much more.\n\n**To start please describe how you have been doing over the past couple months?**"
    };
    setMessages([initialAIMessage]);
  }, []);

  // Scroll on new messages
  useEffect(() => {
    if (!chatWindowRef.current) return;
    const chatEl = chatWindowRef.current;
    const distanceFromBottom =
      chatEl.scrollHeight - (chatEl.scrollTop + chatEl.clientHeight);

    if (!isResponding && distanceFromBottom < 80) {
      chatEl.scrollTo({ top: chatEl.scrollHeight, behavior: "smooth" });
    }
  }, [messages, isResponding]);

  // Show/hide scroll-down button
  useEffect(() => {
    const handleScroll = () => {
      if (!chatWindowRef.current) return;
      const chatEl = chatWindowRef.current;
      const distanceFromBottom =
        chatEl.scrollHeight - (chatEl.scrollTop + chatEl.clientHeight);
      setShowScrollDown(distanceFromBottom > 100);
    };
    if (chatWindowRef.current) {
      chatWindowRef.current.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (chatWindowRef.current) {
        chatWindowRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  // Cleanup intervals on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (displayIntervalRef.current) {
        clearInterval(displayIntervalRef.current);
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  // Guest mode initialization
  function initializeGuestMode() {
    let storedGuestId = localStorage.getItem("analyzeGuestId");
    let storedAgreed = localStorage.getItem("analyzeGuestAgreedToTos") === "true";

    if (!storedGuestId) {
      storedGuestId = generateGuestId();
      localStorage.setItem("analyzeGuestId", storedGuestId);
    }
    setGuestId(storedGuestId);
    setGuestAgreedToTos(storedAgreed);
  }

  function generateGuestId() {
    return "analyze-" + Math.random().toString(36).substring(2, 15);
  }

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("analyzeTheme", newTheme);
  };

  const onDownloadPDF = () => {
    handleDownloadPDF(messages, "analysis-conversation");
  };

  const handleSend = async (message = inputText, isVoiceMessage = false) => {
    if (!message.trim()) return;

    // Store message in a local variable since we're about to clear the input
    const messageToSend = message;

    // Clear input field immediately
    if (message === inputText) {
      setInputText("");
    }

    if (inputRef.current) {
      inputRef.current.style.height = "40px";
      inputRef.current.focus();
    }

    // Guest TOS check - only show if not already in local storage
    if (!guestAgreedToTos && localStorage.getItem("analyzeGuestAgreedToTos") !== "true") {
      setPendingGuestMessage(messageToSend);
      setShowGuestTosModal(true);
      return;
    }

    const userMessage = {
      sender: "user",
      text: messageToSend,
      isVoiceMessage: isVoiceMessage
    };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);

    setIsLoadingResp(true);
    setIsResponding(true);

    // Force scroll to bottom after message is sent
    setTimeout(() => scrollToBottom(), 100);

    await sendAnalysisMessage(updatedMessages, messageToSend);
  };

  const sendAnalysisMessage = async (updatedMessages, message, forceAgreedToTos = false) => {
    try {
      const conversation_history = updatedMessages.map((msg) => ({
        role: msg.sender === "user" ? "user" : "assistant",
        content: msg.text,
      }));

      // API request
      const reqBody = {
        agreedToTos: forceAgreedToTos || guestAgreedToTos,
        message,
        conversation_history,
        appVersion: "analyze-web-1.0",
      };

      const response = await axios.post("/api/analyze/analyze-user-message", reqBody);
      const aiText = response.data.aiText;
      displayAIResponse(aiText);
    } catch (err) {
      console.error(err);
      if (err.response) {
        alert(
          `Error ${err.response.status}: ${err.response.data.error || "Analysis error."
          }`
        );
      } else {
        alert("An unexpected error occurred during analysis.");
      }
      setIsResponding(false);
    } finally {
      setIsLoadingResp(false);
    }
  };

  const processFullAnalysis = async () => {
    try {
      setProcessingAnalysis(true);

      // Create a copy of the messages array
      const messagesWithSummaryRequest = [...messages];

      // Add a final message from the user requesting the summary
      messagesWithSummaryRequest.push({
        sender: "user",
        text: "Reply with a comprehensive psychological profile of the user in markdown format."
      });

      const conversation_history = messagesWithSummaryRequest.map((msg) => ({
        role: msg.sender === "user" ? "user" : "assistant",
        content: msg.text,
      }));

      // API request for full analysis
      const reqBody = {
        agreedToTos: guestAgreedToTos,
        conversation_history,
        appVersion: "analyze-web-1.0",
      };

      const response = await axios.post("/api/analyze/full-conversation", reqBody);
      const analysisText = response.data.aiText;

      // Set the analysis results
      setAnalysisResults(analysisText);
      setShowAnalysisResults(true);
    } catch (err) {
      console.error(err);
      if (err.response) {
        alert(
          `Error ${err.response.status}: ${err.response.data.error || "Analysis processing error."
          }`
        );
      } else {
        alert("An unexpected error occurred during analysis processing.");
      }
    } finally {
      setProcessingAnalysis(false);
    }
  };

  const downloadAnalysisAsPDF = () => {
    if (!analysisResults) return;

    // Create a simplified version of messages for PDF export
    const simplifiedContent = [
      { sender: "ai", text: "Analysis Results" },
      { sender: "ai", text: analysisResults }
    ];

    handleDownloadPDF(simplifiedContent, "complete-psychological-analysis");
  };

  const closeAnalysisResults = () => {
    setShowAnalysisResults(false);
  };

  const openHelpModal = () => {
    setShowHelpModal(true);
  };

  const closeHelpModal = () => {
    setShowHelpModal(false);
  };

  const displayAIResponse = (text) => {
    // Clear any existing display interval
    if (displayIntervalRef.current) {
      clearInterval(displayIntervalRef.current);
    }

    let index = 0;
    const words = text.split(" ");
    const aiMessage = { sender: "ai", text: "" };
    setMessages((prev) => [...prev, aiMessage]);

    // Force scroll to bottom when AI starts responding
    setTimeout(() => scrollToBottom(), 100);

    displayIntervalRef.current = setInterval(() => {
      if (index < words.length) {
        aiMessage.text += (index > 0 ? " " : "") + words[index];
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { ...aiMessage };
          return copy;
        });
        index++;
        // Scroll periodically as AI is typing
        if (index % 10 === 0) {
          scrollToBottom();
        }
      } else {
        clearInterval(displayIntervalRef.current);
        displayIntervalRef.current = null;
        setIsResponding(false);
        if (inputRef.current) {
          inputRef.current.focus();
        }
        // Final scroll after AI response completes
        scrollToBottom();
      }
    }, 30); // Slightly faster typing animation
  };

  // Mic functions
  const onMicClick = async () => {
    if (!recording) {
      setIsVoiceNote(true);
      setRecordedTranscription(null);
      // Start a timer to track recording duration
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } else {
      // Stop the recording duration timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }

    await handleMicClick({
      recording,
      startRecordingFn: async () =>
        startRecording({
          audioChunksRef,
          streamRef,
          mediaRecorderRef,
          setRecording,
          recordingTimeoutRef,
          MAX_RECORDING_TIME: 60,
          handleRecordingStop: (args) =>
            handleRecordingStop({
              ...args,
              setIsTranscribing,
              setRecordedTranscription,
              setIsVoiceNote,
              setRecording,
            }),
        }),
      stopRecordingFn: () =>
        stopRecording({
          mediaRecorderRef,
          recordingTimeoutRef,
          streamRef,
          setRecording,
        }),
    });
  };

  const cancelRecording = () => {
    setIsVoiceNote(false);
    stopRecording({
      mediaRecorderRef,
      recordingTimeoutRef,
      streamRef,
      setRecording,
    });
  };

  const sendVoiceNote = () => {
    if (recordedTranscription) {
      handleSend(recordedTranscription, true);
      setIsVoiceNote(false);
    }
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const scrollToBottom = () => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTo({
        top: chatWindowRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const handleGuestTosAccept = () => {
    // Set both the state and localStorage at the same time
    setGuestAgreedToTos(true);
    localStorage.setItem("analyzeGuestAgreedToTos", "true");
    setShowGuestTosModal(false);

    // Handle the pending message outside the state update
    if (pendingGuestMessage) {
      const messageToSend = pendingGuestMessage;
      setPendingGuestMessage(null);

      // Use a longer delay to ensure all state updates have fully processed
      setTimeout(() => {
        // Directly create and send the message instead of calling handleSend
        // This bypasses the TOS check in handleSend
        const userMessage = {
          sender: "user",
          text: messageToSend,
          isVoiceMessage: false
        };
        const updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);
        setIsLoadingResp(true);
        setIsResponding(true);

        // Pass true for forceAgreedToTos to ensure the API knows the user has agreed
        sendAnalysisMessage(updatedMessages, messageToSend, true);
      }, 300);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Update the settings button ref
  const updateSettingsBtnRef = (elem) => {
    settingsBtnRef.current = elem;
  };

  return (
    <div className={`analyze-container ${theme}`}>
      {/* Header */}
      <header className="analyze-header" style={{
        position: 'sticky',
        top: 0,
        zIndex: '100',
        background: 'inherit',
        borderBottom: '1px solid var(--border-color)'
      }}>
        <div className="header-logo">
          <img
            src={logoSvg}
            alt="Analyze Me Logo"
            className="logo-image"
            style={{ height: '36px', width: 'auto' }}
          />
        </div>
        <div className="header-actions">
          <button
            className="icon-btn"
            onClick={openHelpModal}
            aria-label="Help"
          >
            <FiHelpCircle />
          </button>
          <button
            className="icon-btn"
            onClick={onDownloadPDF}
            aria-label="Download conversation"
          >
            <FiDownload />
          </button>
          <button
            className="icon-btn"
            onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            aria-label="Settings"
            ref={updateSettingsBtnRef}
          >
            <FiSettings />
          </button>
        </div>

        {/* Settings Panel */}
        {isSettingsOpen && (
          <div
            className="settings-panel"
            ref={settingsRef}
            style={{
              position: 'absolute',
              top: '100%',
              right: '0',
              zIndex: '999',
              width: '250px',
              backgroundColor: 'var(--background)',
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
              border: '1px solid var(--border-color)',
              marginTop: '5px'
            }}
          >
            <div className="settings-header">
              <h3>Settings</h3>
            </div>
            <div className="settings-content">
              <div className="settings-item">
                <span className="settings-label">Dark Mode</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={theme === "dark"}
                    onChange={toggleTheme}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Message Counter - Made fixed */}
      <div className="message-counter" style={{
        position: 'sticky',
        top: '70px', /* Height of the nav bar */
        zIndex: '90', /* Lower than the navbar z-index */
        background: 'inherit',
        borderBottom: '1px solid var(--border-color)',
        padding: '8px 0',
        paddingLeft: '16px'
      }}>
        <div className="counter-container">
          <div className="counter-text">
            <span>Messages</span>
            <span className="counter-number">{Math.floor(messages.length / 2)}/5</span>
          </div>

          {showAnalysisButton && (
            <button
              className="analyze-btn"
              onClick={processFullAnalysis}
              disabled={processingAnalysis}
            >
              <FiBarChart2 />
              {processingAnalysis ? "Processing..." : "Get Analysis Results"}
            </button>
          )}
        </div>
      </div>

      {/* Chat Container */}
      <div className="chat-container" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 130px)' }}>
        <div className="messages-container" ref={chatWindowRef} style={{ flex: '1', overflowY: 'auto', paddingBottom: '20px' }}>
          {groupedMessages.map((group, groupIndex) => (
            <div key={groupIndex} className={`message-group ${group.sender}`}>
              {group.messages.map((message, msgIndex) => (
                <div key={`${groupIndex}-${msgIndex}`} className={`message ${message.sender}`}>
                  {message.isVoiceMessage && (
                    <div className="voice-indicator">
                      <span>Voice message</span>
                    </div>
                  )}

                  <div className="message-content">
                    <ReactMarkdown
                      children={message.text}
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer" />
                        ),
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ))}

          {isLoadingResp && (
            <div className="message-group ai">
              <div className="message ai">
                <div className="typing-indicator">
                  <span>Thinking</span>
                  <div className="typing-dots">
                    <span className="typing-dot"></span>
                    <span className="typing-dot"></span>
                    <span className="typing-dot"></span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="input-area" style={{ position: 'sticky', bottom: 0, background: 'var(--background)', borderTop: '1px solid var(--border-color)', paddingTop: '10px', zIndex: 10 }}>
          {isVoiceNote ? (
            <div className="input-container">
              <div className="recording-interface">
                <div className="recording-header">
                  <div className="recording-indicator">
                    <div className="recording-pulse"></div>
                    <span className="recording-time">{formatTime(recordingDuration)}</span>
                  </div>
                  <div className="recording-status">
                    {isTranscribing
                      ? "Transcribing..."
                      : recordedTranscription
                        ? "Ready to send"
                        : "Recording..."}
                  </div>
                </div>

                {recordedTranscription && (
                  <div className="recording-preview">{recordedTranscription}</div>
                )}

                <div className="recording-actions">
                  <button
                    className="recording-button cancel"
                    onClick={cancelRecording}
                    aria-label="Cancel recording"
                  >
                    <IoMdClose />
                  </button>

                  {recordedTranscription ? (
                    <button
                      className="recording-button send"
                      onClick={sendVoiceNote}
                      aria-label="Send voice message"
                    >
                      <IoSend />
                    </button>
                  ) : (
                    <button
                      className="recording-button stop"
                      onClick={onMicClick}
                      aria-label="Stop recording"
                    >
                      <BsPauseFill />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="input-container">
              <textarea
                className="input-textarea"
                placeholder="Message..."
                value={inputText}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                ref={inputRef}
                rows={1}
              />
              <button
                className="input-button"
                onClick={onMicClick}
                aria-label="Record voice message"
              >
                <FaMicrophone />
              </button>
              <button
                className={`input-button send`}
                onClick={() => handleSend()}
                aria-label="Send message"
                disabled={!inputText.trim()}
              >
                <IoSend />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Scroll Button */}
      {showScrollDown && (
        <button className="scroll-button" onClick={scrollToBottom}>
          <FiChevronDown />
        </button>
      )}

      {/* TOS Modal */}
      {showGuestTosModal && (
        <div className="modal-overlay">
          <div className="tos-modal">
            <div className="tos-header">
              <h2>Please Agree to the Terms of Service</h2>
            </div>
            <div className="tos-content">
              <p>
                By using the Analysis feature, you agree to our{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>.
              </p>
              <p>
                Unlike chat messages, analysis conversations are not limited and are meant
                for exploring patterns, themes, and insights from a psychological perspective.
              </p>
            </div>
            <div className="tos-footer">
              <button className="tos-button" onClick={handleGuestTosAccept}>
                I Agree
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelpModal && (
        <div className="modal-overlay">
          <div className="tos-modal">
            <div className="tos-header">
              <h2>How to Use Analyze Me</h2>
            </div>
            <div className="tos-content">
              <p><strong>Welcome to Analyze Me!</strong> This tool helps you gain psychological insights through conversation.</p>

              <p><strong>Getting Started:</strong></p>
              <ul>
                <li>Share your thoughts, experiences, behaviors, or any text you'd like analyzed</li>
                <li>Record voice messages by clicking the microphone icon</li>
                <li>After 5 messages, you'll unlock a comprehensive analysis</li>
              </ul>

              <p><strong>Features:</strong></p>
              <ul>
                <li>Download your conversation as a PDF</li>
                <li>Switch between light and dark mode</li>
                <li>Get a detailed psychological analysis after 5 messages</li>
              </ul>
            </div>
            <div className="tos-footer">
              <button className="tos-button" onClick={closeHelpModal}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Results Modal */}
      {showAnalysisResults && analysisResults && (
        <div className="modal-overlay">
          <div className="results-modal">
            <div className="results-header">
              <h2>Your Psychological Analysis</h2>
              <button
                className="close-button"
                onClick={closeAnalysisResults}
                aria-label="Close results"
              >
                <IoMdClose />
              </button>
            </div>
            <div className="results-content">
              <ReactMarkdown
                children={analysisResults}
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ node, ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer" />
                  ),
                }}
              />
            </div>
            <div className="results-footer">
              <button
                className="download-button"
                onClick={downloadAnalysisAsPDF}
                aria-label="Download analysis"
              >
                <FiDownload /> Download Analysis
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalyzeMePage; 